import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import Dropdown from "../../common/dropDown";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import { renderAppointmentStatusPill } from "../eplanner/eplannerReportUtils";
import {
  buildAppointmentQueryPayload,
  fetchAppointments,
  fetchPlannerStudyBundle,
} from "../eplanner/eplannerService";
import { lightTintFromColor } from "../eplanner/appointments/AppointmentCardList";
import {
  fetchVisitWiseStudyInfo,
  fetchVolunteerStudySummary,
  getVolunteerDisplayName,
  saveAppointmentPayment,
  sendPaymentEmail,
} from "./participantService";
import { isVolunteerProfileReadOnly } from "./participantReportUtils";
import {
  buildStudyColorLookup,
  normStudyId,
  pickStudyColorRaw,
  resolveStudyDisplayColor,
} from "../../../utils/studyColorUtils";

const PAYMENT_FILTER_OPTIONS = [
  { value: "all", label: "All payments" },
  { value: "unpaid", label: "Unpaid only" },
  { value: "paid", label: "Paid only" },
];

function placePopupNearAnchor(anchorEl, { width = 260, height = 180 } = {}) {
  const rect = anchorEl?.getBoundingClientRect?.();
  if (!rect || (!rect.width && !rect.height)) return { top: 8, left: 8 };
  const gap = 8;
  const pad = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const menuW = Math.max(160, Number(width) || 260);
  const menuH = Math.max(120, Number(height) || 180);

  let left = rect.left;
  let top = rect.bottom + gap;

  if (top + menuH > vh - pad) {
    const above = rect.top - menuH - gap;
    const belowClamped = vh - pad - menuH;
    if (above >= pad && above + menuH >= rect.top - 12) {
      top = above;
    } else {
      top = Math.max(pad, belowClamped);
    }
  }

  if (left + menuW > vw - pad) left = vw - pad - menuW;
  if (left < pad) left = pad;
  top = Math.min(Math.max(pad, top), Math.max(pad, vh - menuH - pad));
  left = Math.min(Math.max(pad, left), Math.max(pad, vw - menuW - pad));
  return { top, left };
}

function resolveRowStudyColor(app, study, colorLookup) {
  const sid = normStudyId(
    pick(app, "StudyId", "StudyID", "studyId") ||
      pick(study, "StudyId", "StudyID", "studyId"),
  );
  const fromLookup = sid && colorLookup?.get?.(sid);
  return resolveStudyDisplayColor(
    pickStudyColorRaw(app) ||
      pickStudyColorRaw(study) ||
      fromLookup ||
      "",
  );
}

function pick(row, ...keys) {
  if (!row) return "";
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
}

function studyIdOf(row) {
  return String(pick(row, "StudyId", "StudyID", "studyId") || "").trim();
}

function appointmentIdOf(row) {
  return String(
    pick(row, "AppointmentId", "AppointmentID", "appointmentId") || "",
  ).trim();
}

function isPaid(row) {
  return Boolean(String(pick(row, "AppPaymentId") || "").trim());
}

function scheduledPayment(row, study) {
  const visitName = String(pick(row, "VisitName") || "");
  if (isPaid(row)) {
    return pick(row, "AppointmentPerVisitPayment", "VisitPayment") || "0";
  }
  if (
    visitName === "Adhoc Visit" &&
    pick(study, "AdhocPayment") &&
    pick(study, "AdhocPayment") !== "N/A"
  ) {
    return pick(row, "VisitPayment") || pick(study, "AdhocPayment");
  }
  return pick(row, "VisitPayment") || "0";
}

function parseAmount(value) {
  const num = Number(String(value ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(num) ? num : 0;
}

function normalizeMoneyDisplay(value) {
  const text = String(value ?? "").trim();
  if (!text || text.toUpperCase() === "N/A") return "";
  const cleaned = text.replace(/[^\d.-]/g, "");
  return cleaned || text;
}

function PaymentInlineEdit({
  row,
  amount,
  onAmountChange,
  onSave,
  saving = false,
}) {
  const wrapRef = useRef(null);
  const popoverRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [draft, setDraft] = useState(amount || "");

  const shownActual = normalizeMoneyDisplay(amount) || "0";

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (wrapRef.current?.contains(event.target)) return;
      if (popoverRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const openEditor = () => {
    // Edit Actual Payment — prefer current actual, else fall back to scheduled.
    const currentActual = normalizeMoneyDisplay(amount);
    const scheduled = normalizeMoneyDisplay(row.scheduled);
    setDraft(currentActual || scheduled || "");
    setPos(
      placePopupNearAnchor(wrapRef.current, { width: 260, height: 200 }),
    );
    setOpen(true);
  };

  const commit = async () => {
    onAmountChange?.(draft);
    await onSave?.(draft);
    setOpen(false);
  };

  return (
    <div className="pm-appts__pay-cell" ref={wrapRef}>
      <span className={`pm-appts__pay-value${row.paid ? " is-paid" : ""}`}>
        {row.currency}
        {shownActual}
      </span>
      <button
        type="button"
        className="pm-appts__pay-edit-btn"
        onClick={openEditor}
        title={row.paid ? "Update actual payment" : "Save actual payment"}
        aria-label={row.paid ? "Update actual payment" : "Save actual payment"}
        aria-expanded={open}
      >
        <i className="fas fa-pencil-alt" aria-hidden="true" />
      </button>
      {open
        ? createPortal(
            <div
              ref={popoverRef}
              className="pm-inline-edit__popover"
              role="dialog"
              aria-label="Edit actual payment"
              style={{ top: pos.top, left: pos.left, width: 260 }}
              onClick={(e) => e.stopPropagation()}
              data-pm-inline-popover="1"
            >
              <div className="pm-inline-edit__title">
                {row.paid ? "Update actual payment" : "Save actual payment"}
              </div>
              <div className="pm-appts__pay-popup-meta">
                <div className="pm-appts__pay-popup-visit">{row.visitName}</div>
                <div className="pm-appts__pay-popup-time">{row.startTime}</div>
                <div className="pm-appts__pay-popup-scheduled">
                  Scheduled: {row.currency}
                  {normalizeMoneyDisplay(row.scheduled) || "0"}
                </div>
              </div>
              <label className="pm-inline-edit__label">
                Actual payment ({row.currency})
                <input
                  type="text"
                  className="form-control form-control-sm"
                  maxLength={6}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  disabled={saving}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commit();
                    }
                  }}
                />
              </label>
              <div className="pm-inline-edit__actions">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={commit}
                  disabled={saving}
                >
                  {saving ? "Saving…" : row.paid ? "Update" : "Save"}
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function buildPaymentEmailHtml(studyName, currency, rows) {
  let total = 0;
  let body = `
    <table border="1" cellpadding="8" cellspacing="0" style="border-collapse: collapse; width: 100%; text-align: left;">
      <thead style="background-color: #cce5ff;">
        <tr>
          <th>Visit name</th>
          <th>Appointment Date &amp; Time</th>
          <th>Payment to be received</th>
        </tr>
      </thead>
      <tbody>`;
  rows.forEach((row) => {
    const amount = parseAmount(row.paymentReceived || row.paymentToBeReceived);
    total += amount;
    body += `
        <tr>
          <td>${studyName} - ${row.visitName}</td>
          <td>${row.startTime || ""}</td>
          <td>${currency}${amount}</td>
        </tr>`;
  });
  body += `
        <tr style="font-weight: bold;">
          <td colspan="2">Total Payments</td>
          <td>${currency}${total}</td>
        </tr>
      </tbody>
    </table>`;
  return body;
}

function StudyHistoryPanel({
  userData,
  volunteerId,
  volunteer,
  canEdit = false,
  onChanged,
}) {
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const [studyOptions, setStudyOptions] = useState([]);
  const [studyFilter, setStudyFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [paymentFilter, setPaymentFilter] = useState(
    PAYMENT_FILTER_OPTIONS[0],
  );
  const [flatRows, setFlatRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [manualAmounts, setManualAmounts] = useState({});
  const [studyColorLookup, setStudyColorLookup] = useState(() => new Map());
  const studyColorLookupRef = useRef(studyColorLookup);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    studyColorLookupRef.current = studyColorLookup;
  }, [studyColorLookup]);

  const volunteerActive = !isVolunteerProfileReadOnly(volunteer);

  const studyFilterIds = useMemo(
    () =>
      (Array.isArray(studyFilter) ? studyFilter : [])
        .map((item) => (typeof item === "string" ? item : item?.value))
        .filter(Boolean),
    [studyFilter],
  );

  const statusFilterValues = useMemo(
    () =>
      (Array.isArray(statusFilter) ? statusFilter : [])
        .map((item) => (typeof item === "string" ? item : item?.value))
        .filter(Boolean),
    [statusFilter],
  );

  const loadStudyOptions = useCallback(async () => {
    if (!userData?.OrganizationId || !volunteerId) return;
    try {
      const [summary, plannerBundle] = await Promise.all([
        fetchVolunteerStudySummary(userData, volunteerId),
        fetchPlannerStudyBundle(userData).catch(() => ({ studies: [] })),
      ]);
      const studies = Array.isArray(summary.studies) ? summary.studies : [];
      const plannerStudies = Array.isArray(plannerBundle?.studies)
        ? plannerBundle.studies
        : [];
      const colorLookup = buildStudyColorLookup([
        ...plannerStudies,
        ...studies,
      ]);
      setStudyColorLookup(colorLookup);
      const options = studies
        .map((row) => {
          const value = studyIdOf(row);
          const label = String(
            pick(row, "StudyName", "studyName") || value || "",
          ).trim();
          if (!value) return null;
          return { value, label };
        })
        .filter(Boolean);
      setStudyOptions(options);
      setStudyFilter(options);
    } catch {
      setStudyOptions([]);
      setStudyFilter([]);
      setStudyColorLookup(new Map());
    }
  }, [userData, volunteerId]);

  const loadHistory = useCallback(async () => {
    if (!userData?.OrganizationId || !volunteerId) return;
    if (!studyOptions.length) {
      setFlatRows([]);
      return;
    }
    const loadIds = studyOptions.map((o) => o.value);
    setLoading(true);
    try {
      const [{ studies, appointments }, listResult] = await Promise.all([
        fetchVisitWiseStudyInfo(userData, volunteerId, loadIds).catch(() => ({
          studies: [],
          appointments: [],
        })),
        fetchAppointments(
          buildAppointmentQueryPayload({
            userData,
            actionType: "AppointmentListNew",
            appointmentPeriod: "ALLTIME",
            studyIds: loadIds,
            volunteerId,
          }),
        ).catch(() => ({ rows: [] })),
      ]);

      const studyList = Array.isArray(studies) ? studies : [];
      const appList = Array.isArray(appointments) ? appointments : [];
      const listRows = Array.isArray(listResult?.rows) ? listResult.rows : [];
      const mergedLookup = new Map(studyColorLookupRef.current);
      buildStudyColorLookup([...studyList, ...listRows]).forEach((color, id) => {
        if (color) mergedLookup.set(id, color);
      });
      studyColorLookupRef.current = mergedLookup;
      setStudyColorLookup(mergedLookup);

      const studyMap = new Map(studyList.map((s) => [studyIdOf(s).toUpperCase(), s]));
      studyOptions.forEach((opt) => {
        const key = String(opt.value || "").toUpperCase();
        if (!studyMap.has(key)) {
          studyMap.set(key, { StudyId: opt.value, StudyName: opt.label });
        }
      });

      const toRow = (app, study) => {
        const sid = studyIdOf(app) || studyIdOf(study);
        const paid = isPaid(app);
        const scheduled = scheduledPayment(app, study);
        const groupName = String(
          pick(app, "GroupName") || pick(study, "GroupName") || "",
        ).trim();
        const paymentFlag = String(
          pick(study, "PaymentEditable") || "1",
        ).trim();
        const paymentEditable =
          volunteerActive &&
          canEdit &&
          paymentFlag !== "0" &&
          String(pick(study, "StudyStatus")) !== "Archive";
        const actualRaw = pick(app, "AppPayment", "Payment") || "0";
        const actualDisplay = normalizeMoneyDisplay(actualRaw) || "0";
        return {
          id:
            appointmentIdOf(app) ||
            `${sid}-${pick(app, "VisitName")}-${pick(app, "StartTime", "AppointmentDate")}`,
          studyId: sid,
          studyName:
            pick(study, "StudyName") || pick(app, "StudyName") || "—",
          studyNumber:
            pick(app, "StudyNumber") || pick(study, "StudyNumber") || "",
          groupName:
            !groupName || groupName === "null" ? "Study Screening" : groupName,
          studyPayment: pick(study, "StudyPayment") || "0",
          visitName: pick(app, "VisitName") || "—",
          startTime:
            pick(app, "StartTime", "AppointmentDate", "StartDate") || "—",
          status: pick(app, "AppointmentStatus", "Status") || "—",
          scheduled,
          actual: actualDisplay,
          currency: pick(app, "CurrencySymbol") || "£",
          paid,
          paymentEditable,
          visitIdRef: pick(app, "VisitIdRef", "VisitId") || "",
          appPaymentId: pick(app, "AppPaymentId") || "",
          studyColor: resolveRowStudyColor(app, study, mergedLookup),
          study,
        };
      };

      let rows = appList
        .map((app) => {
          const study = studyMap.get(studyIdOf(app).toUpperCase()) || {};
          return toRow(app, study);
        })
        .filter((row) => row.id);

      // VisitWiseStudyInfo sometimes returns studies without visit rows — fall back to ePlanner list.
      if (!rows.length) {
        rows = listRows.map((app) => {
          const study = studyMap.get(studyIdOf(app).toUpperCase()) || {
            StudyId: studyIdOf(app),
            StudyName: pick(app, "StudyName"),
          };
          return toRow(app, study);
        });
      } else {
        const listById = new Map(
          listRows.map((app) => [appointmentIdOf(app).toUpperCase(), app]),
        );
        rows = rows.map((row) => {
          const extra = listById.get(String(row.id).toUpperCase());
          if (!extra) return row;
          return {
            ...row,
            studyNumber:
              row.studyNumber || pick(extra, "StudyNumber") || row.studyNumber,
            studyColor:
              resolveRowStudyColor(extra, row.study, mergedLookup) ||
              row.studyColor,
            startTime:
              row.startTime !== "—"
                ? row.startTime
                : pick(extra, "StartTime", "AppointmentDate") || row.startTime,
            status:
              row.status !== "—"
                ? row.status
                : pick(extra, "AppointmentStatus") || row.status,
          };
        });
      }

      rows.sort((a, b) =>
        String(b.startTime).localeCompare(String(a.startTime)),
      );
      setFlatRows(rows);
      setSelectedIds(new Set());
      const amounts = {};
      rows.forEach((row) => {
        amounts[row.id] = row.actual || "";
      });
      setManualAmounts(amounts);
    } catch (err) {
      setFlatRows([]);
      showAlert({
        message: err?.message || "Could not load appointments.",
      });
    } finally {
      setLoading(false);
    }
  }, [canEdit, showAlert, studyOptions, userData, volunteerActive, volunteerId]);

  useEffect(() => {
    loadStudyOptions();
  }, [loadStudyOptions]);

  useEffect(() => {
    if (studyOptions.length) loadHistory();
  }, [loadHistory, studyOptions.length]);

  const statusOptions = useMemo(() => {
    const set = new Set();
    flatRows.forEach((row) => {
      if (row.status && row.status !== "—") set.add(row.status);
    });
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));
  }, [flatRows]);

  const visibleRows = useMemo(() => {
    return flatRows.filter((row) => {
      if (studyFilterIds.length && !studyFilterIds.includes(row.studyId)) {
        return false;
      }
      if (
        statusFilterValues.length &&
        !statusFilterValues.includes(row.status)
      ) {
        return false;
      }
      const pay = paymentFilter?.value || "all";
      if (pay === "paid" && !row.paid) return false;
      if (pay === "unpaid" && row.paid) return false;
      return true;
    });
  }, [flatRows, paymentFilter, statusFilterValues, studyFilterIds]);

  const hasFilters =
    studyFilterIds.length !== studyOptions.length ||
    statusFilterValues.length > 0 ||
    (paymentFilter?.value || "all") !== "all";

  const clearFilters = () => {
    setStudyFilter(studyOptions);
    setStatusFilter([]);
    setPaymentFilter(PAYMENT_FILTER_OPTIONS[0]);
  };

  const toggleEmailSelect = (row, checked) => {
    if (!row.paid) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(row.id);
      else next.delete(row.id);
      return next;
    });
  };

  const paidSelected = visibleRows.filter(
    (row) => row.paid && selectedIds.has(row.id),
  );

  const handleSaveRow = async (row, amountOverride) => {
    const amount = normalizeMoneyDisplay(
      amountOverride ??
        manualAmounts[row.id] ??
        row.actual ??
        row.scheduled ??
        "",
    );
    if (!amount || amount === "0") {
      showAlert({
        message: `Enter an actual payment amount for ${row.visitName}.`,
      });
      return;
    }

    const amountNumber = parseAmount(amount);
    if (!amountNumber) {
      showAlert({
        message: `Enter a valid payment amount for ${row.visitName}.`,
      });
      return;
    }

    // Unpaid rows have no AppPaymentId — must send null (not "") so the SP inserts.
    // Empty string hits the UPDATE branch and fails uniqueidentifier conversion.
    const appPaymentId = String(row.appPaymentId || "").trim() || null;
    const appointmentId = String(row.id || "").trim();
    if (
      !appointmentId ||
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        appointmentId,
      )
    ) {
      showAlert({
        message:
          "Could not save payment — appointment id is missing. Refresh and try again.",
      });
      return;
    }

    const isAdhoc =
      String(row.visitName || "").trim().toUpperCase() === "ADHOC VISIT";
    const visitIdRef = isAdhoc
      ? null
      : String(row.visitIdRef || "").trim() || null;

    setManualAmounts((prev) => ({ ...prev, [row.id]: amount }));
    showLoader("Saving payment…");
    try {
      await saveAppointmentPayment(userData, {
        StudyIdRef: row.studyId,
        AdhocVisit: isAdhoc,
        ActionType: "Save",
        VolunteerIdRef: volunteerId,
        VolunteerName: getVolunteerDisplayName(volunteer),
        StudyName: row.studyName,
        AppointmentPaymentList: [
          {
            AppPaymentId: appPaymentId,
            AppointmentIdRef: appointmentId,
            VisitIdRef: visitIdRef,
            Amount: amountNumber,
            VisitName: row.visitName,
          },
        ],
      });
      showAlert({ message: "Payment saved.", type: "success" });
      await loadHistory();
      await onChanged?.();
    } catch (err) {
      showAlert({ message: err?.message || "Could not save payment." });
    } finally {
      hideLoader();
    }
  };

  const handleSendPaymentEmail = async () => {
    if (!paidSelected.length) {
      showAlert({
        message: "Select paid visits (email checkbox) to send a summary.",
      });
      return;
    }

    const byStudy = new Map();
    paidSelected.forEach((row) => {
      const list = byStudy.get(row.studyId) || [];
      list.push(row);
      byStudy.set(row.studyId, list);
    });

    showLoader("Sending payment email…");
    try {
      for (const [, rows] of byStudy) {
        const currency = rows[0].currency || "£";
        const emailRows = rows.map((row) => ({
          visitName: row.visitName,
          startTime: row.startTime,
          paymentToBeReceived: row.scheduled,
          paymentReceived: row.actual,
          currency: row.currency,
        }));
        await sendPaymentEmail(userData, {
          StudyIdRef: rows[0].studyId,
          AdhocVisit: false,
          ActionType: "SendPaymentEmail",
          VolunteerIdRef: volunteerId,
          VolunteerName: getVolunteerDisplayName(volunteer),
          StudyName: rows[0].studyName,
          SummaryTable: buildPaymentEmailHtml(
            rows[0].studyName,
            currency,
            emailRows,
          ),
        });
      }
      showAlert({ message: "Payment email sent.", type: "success" });
      setSelectedIds(new Set());
      await onChanged?.();
    } catch (err) {
      showAlert({ message: err?.message || "Could not send payment email." });
    } finally {
      hideLoader();
    }
  };

  if (!studyOptions.length && !loading) {
    return (
      <div className="pm-empty-state">
        No studies linked to this participant yet.
      </div>
    );
  }

  return (
    <div className="pm-appts">
      <div className="pm-appts__main">
        {loading ? (
          <div className="pm-empty-state">Loading appointments…</div>
        ) : !visibleRows.length ? (
          <div className="pm-empty-state pm-appts__empty">
            No appointments match the current filters.
            {hasFilters ? (
              <button
                type="button"
                className="btn btn-link btn-sm px-0"
                onClick={clearFilters}
              >
                Clear filters
              </button>
            ) : null}
          </div>
        ) : (
          <div className="pm-appts__table-wrap pm-thin-scroll">
            <table className="pm-appts__table">
              <thead>
                <tr>
                  <th>Study</th>
                  <th>Visit</th>
                  <th>Date</th>
                  <th>Payment</th>
                  <th>Actual payment</th>
                  <th>Payment status</th>
                  <th className="pm-appts__check" title="Select for email">
                    Email
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => {
                  const editable = row.paymentEditable;
                  const actualValue = manualAmounts[row.id] ?? row.actual ?? "";
                  const studyNo = String(row.studyNumber || "").trim();
                  const studyColor = resolveStudyDisplayColor(
                    row.studyColor ||
                      studyColorLookup.get(normStudyId(row.studyId)) ||
                      "",
                  );
                  const badgeBg = lightTintFromColor(studyColor, 0.86);
                  const badgeBorder = lightTintFromColor(studyColor, 0.62);
                  return (
                    <tr key={row.id}>
                      <td>
                        <div className="pm-appts__study">
                          <div className="pm-appts__study-line">
                            {studyNo ? (
                              <span
                                className="pm-appts__study-badge"
                                title={studyNo}
                                style={{
                                  background: badgeBg,
                                  borderColor: badgeBorder,
                                }}
                              >
                                <span
                                  className="pm-appts__study-dot"
                                  style={{ background: studyColor }}
                                  aria-hidden="true"
                                />
                                {studyNo}
                              </span>
                            ) : null}
                            <span
                              className="pm-appts__study-name"
                              title={row.studyName}
                            >
                              {row.studyName}
                            </span>
                          </div>
                          <span className="pm-appts__study-meta">
                            {row.groupName}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="pm-appts__visit">
                          <span className="pm-appts__visit-name">
                            {row.visitName}
                          </span>
                          {renderAppointmentStatusPill(row.status)}
                        </div>
                      </td>
                      <td className="pm-appts__date">{row.startTime}</td>
                      <td>
                        {row.currency}
                        {normalizeMoneyDisplay(row.scheduled) || "0"}
                      </td>
                      <td>
                        {editable ? (
                          <PaymentInlineEdit
                            row={row}
                            amount={actualValue}
                            onAmountChange={(next) =>
                              setManualAmounts((prev) => ({
                                ...prev,
                                [row.id]: next,
                              }))
                            }
                            onSave={(next) => handleSaveRow(row, next)}
                          />
                        ) : (
                          <span
                            className={row.paid ? "pm-appts__paid" : undefined}
                          >
                            {row.currency}
                            {row.actual}
                          </span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`pm-appts__pay-status ${
                            row.paid
                              ? "pm-appts__pay-status--paid"
                              : "pm-appts__pay-status--unpaid"
                          }`}
                        >
                          {row.paid ? "Paid" : "Unpaid"}
                        </span>
                      </td>
                      <td className="pm-appts__check">
                        {row.paid ? (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(row.id)}
                            onChange={(e) =>
                              toggleEmailSelect(row, e.target.checked)
                            }
                            title="Select for payment email"
                            aria-label={`Email ${row.visitName}`}
                          />
                        ) : (
                          <span className="pm-appts__muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <aside
        className="pm-appts__filters pm-thin-scroll"
        aria-label="Appointment filters"
      >
        <div className="pm-appts__filters-head">
          <h3 className="pm-appts__filters-title">Filters</h3>
          {hasFilters ? (
            <button
              type="button"
              className="btn btn-link btn-sm px-0"
              onClick={clearFilters}
            >
              Reset
            </button>
          ) : null}
        </div>

        <div className="pm-appts__side-meta">
          <div className="pm-appts__count">
            {loading
              ? "Loading…"
              : `${visibleRows.length} appointment${visibleRows.length === 1 ? "" : "s"}`}
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm w-100"
            onClick={loadHistory}
            disabled={loading}
          >
            Refresh
          </button>
          {canEdit && paidSelected.length ? (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm w-100"
              onClick={handleSendPaymentEmail}
            >
              Send payment email ({paidSelected.length})
            </button>
          ) : null}
        </div>

        <div className="pm-appts__field">
          <span>Studies</span>
          <Dropdown
            options={studyOptions}
            isMulti
            selected={studyFilter}
            onSelect={setStudyFilter}
            showSearch
            showMultiSelectedTooltip
          />
        </div>

        <div className="pm-appts__field">
          <span>Status</span>
          <Dropdown
            options={statusOptions}
            isMulti
            selected={statusFilter}
            onSelect={setStatusFilter}
            showSearch
            showMultiSelectedTooltip
          />
        </div>

        <div className="pm-appts__field">
          <span>Payment</span>
          <Dropdown
            options={PAYMENT_FILTER_OPTIONS}
            selected={paymentFilter}
            onSelect={(opt) =>
              setPaymentFilter(opt || PAYMENT_FILTER_OPTIONS[0])
            }
            showSearch={false}
          />
        </div>

        <p className="pm-appts__hint">
          Pencil on Actual payment updates the received amount. Use Email on
          paid rows to send a summary.
        </p>
      </aside>

      <InfoAlert
        isOpen={alertState.isOpen}
        message={alertState.message}
        type={alertState.type}
        onClose={hideAlert}
      />
    </div>
  );
}

export default StudyHistoryPanel;
